import { IOfficeLocationsSearchCriteria, IAvailableTimeslotsSearchCriteria, ISelectedTimeslot } from './types';
import { ClientService } from '../../shared/api/ClientService';
import moment from 'moment';

export const argumentifyOfficeLocationsSearchCriteria = ({
  filterText,
  nearbyLatitude,
  nearbyLongitude,
  includeInactive,
  isActive,
  sorting,
  skipCount,
  maxResultCount,
}: IOfficeLocationsSearchCriteria) =>
  [filterText, nearbyLatitude, nearbyLongitude, includeInactive, isActive, sorting, skipCount, maxResultCount] as const;

export const argumentifyTimeslotsSearchCriteria = ({
  fileId,
  date,
  officeLocationId,
  timeRange,
  meetingTypeId,
  appointmentTypeId,
  teamNames,
  duration,
}: IAvailableTimeslotsSearchCriteria) =>
  [fileId, date, officeLocationId, timeRange, meetingTypeId, appointmentTypeId, teamNames, duration] as const;

export const convertMeetingTypeToReadable = (
  meetingType?: ClientService.MeetingTypeDto,
  agent?: ClientService.AvailableAgentDto
) => {
  if (!meetingType) return '-';
  if (!agent) return meetingType?.name;
  return `${meetingType?.name} with ${agent?.firstName || ''} ${agent.lastName || ''}`;
};

export const convertOfficeToReadable = (office?: ClientService.OfficeLocationDto) => {
  if (!office) return 'Office: -';
  return `${office?.name || '-'}: ${office?.streetName || ''} ${office?.streetNumber || ''} ${office?.unit || ''}, ${
    office?.city || ''
  } ${office?.postalCode || '-'}`;
};

export const getAvailableTimeslotsByMeetingType = (
  appointmentTimes?: ClientService.AppointmentTimeSlotDto[],
  meetingTypeId?: string
) =>
  appointmentTimes?.filter((item) => {
    if (!meetingTypeId) return item?.availableAgents;

    const isFound = item?.availableAgents?.find((agent) =>
      agent?.meetingTypes?.find((type) => type.id === meetingTypeId)
    );
    if (isFound) {
      return item?.availableAgents?.filter((agent) => agent?.meetingTypes?.find((type) => type.id === meetingTypeId));
    }

    return false;
  });

export const getMeetingTypeIfOnlyOneAvailable = (
  appointmentTimes?: ClientService.AppointmentTimeSlotDto[],
  meetingTypes?: ClientService.LookupDto[]
) => {
  const inPersonMeetingTypeId = meetingTypes?.find(
    (item) => item.enumValue === ClientService.MeetingTypeEnum.InPerson
  )?.id;
  const phoneVideoMeetingTypeId = meetingTypes?.find(
    (item) => item.enumValue === ClientService.MeetingTypeEnum.Phone
  )?.id;
  const countInPerson = getAvailableTimeslotsByMeetingType(appointmentTimes, inPersonMeetingTypeId)?.length;
  const countPhoneVideo = getAvailableTimeslotsByMeetingType(appointmentTimes, phoneVideoMeetingTypeId)?.length;
  if (!countInPerson && countPhoneVideo) return phoneVideoMeetingTypeId;
  if (!countPhoneVideo && countInPerson) return inPersonMeetingTypeId;
  return undefined;
};

export const setTimeslotSpanSize = (
  meetingTypeId?: string,
  timeslotMeetingTypeEnum?: ClientService.MeetingTypeEnum,
  meetingTypes?: ClientService.LookupDto[]
) => {
  const meetingType = meetingTypes?.find((item) => item?.id === meetingTypeId);

  if (!Boolean(meetingType)) return 9;
  if (meetingType?.enumValue === timeslotMeetingTypeEnum) return 18;
  return 0;
};

/**
 * Disabled. Replaced by new method getAgentByPreselectRuleForAppointment. #20024
 * @param agents
 * @param meetingTypeId
 * @returns
 */
export const getAgentByPreselectRule = (agents?: ClientService.AvailableAgentDto[], meetingTypeId?: string) => {
  const availablaAgentsByMeetingType = agents?.filter((agent: ClientService.AvailableAgentDto) =>
    agent?.meetingTypes?.find((type) => type.id === meetingTypeId)
  );

  const sorted = availablaAgentsByMeetingType?.sort((a, b) => {
    if (Number(a?.noOfPDSAppointmentsForDay) - Number(b?.noOfPDSAppointmentsForDay) === 0) {
      return Number(a?.weeklyAverageOfPDSAppointments) - Number(b?.weeklyAverageOfPDSAppointments);
    }
    return Number(a?.noOfPDSAppointmentsForDay) - Number(b?.noOfPDSAppointmentsForDay);
  });

  return sorted?.[0]?.agentId;
};

// TODO. Enable back in R4
/**
 * Note: Sorting by noOfPDSAppointmentsForDay has completed in server side, no need to do it again here.
 * Also need to consider select available agent based on current debtor's preferred language code.
 * Above processes has complated in server side, here just simply pickup the first item from the drop down list of available agents.
 * #20024
 *
 * @returns one available agent's Id. Note: It is always the first item in the drop down.
 */
export const getAgentByPreselectRuleForAppointment = (
  agents?: ClientService.AvailableAgentDto[],
  meetingTypeId?: string
) => {
  const availableAgentsByMeetingType = agents?.filter((agent: ClientService.AvailableAgentDto) =>
    agent?.meetingTypes?.find((type) => type.id === meetingTypeId)
  );

  return availableAgentsByMeetingType?.[0]?.agentId;
};

export interface IPreserveResult {
  availableTimeslots?: ClientService.AppointmentTimeSlotDto[];
  selectedTimeslot?: ISelectedTimeslot;
  agentId?: string;
}

const timeslotMatchesByTime = (a?: ClientService.AppointmentTimeSlotDto, b?: ClientService.AppointmentTimeSlotDto) => {
  if (!a || !b || !a.time || !b.time) return false;
  return moment(a.time).isSame(moment(b.time));
};

const isAgentValidForTimeslot = (
  timeslot?: ClientService.AppointmentTimeSlotDto,
  agentId?: string,
  meetingTypeId?: string
) => {
  if (!timeslot || !agentId) return false;
  return Boolean(
    timeslot.availableAgents?.some(
      (agent) =>
        agent.agentId === agentId &&
        (meetingTypeId ? agent.meetingTypes?.some((type) => type.id === meetingTypeId) : true)
    )
  );
};

export const preserveSelection = (
  newTimeslots?: ClientService.AppointmentTimeSlotDto[],
  meetingTypeId?: string,
  prevSelectedTimeslot?: ISelectedTimeslot,
  prevAgentId?: string
): IPreserveResult => {
  const availableTimeslots = getAvailableTimeslotsByMeetingType(newTimeslots, meetingTypeId) || [];

  if (!availableTimeslots.length) {
    return { availableTimeslots: [], selectedTimeslot: undefined, agentId: undefined };
  }

  // Try to find the same time slot in the new list
  const matchingTimeslot = availableTimeslots.find((ts) => timeslotMatchesByTime(ts, prevSelectedTimeslot));

  // Determine meeting type to use for the selected timeslot
  const resolveMeetingTypeId = (
    slot?: ClientService.AppointmentTimeSlotDto,
    desiredMeetingTypeId?: string,
    previousTimeslotMeetingTypeId?: string
  ) => {
    if (!slot) return undefined;
    // If a specific meeting type is selected (In-Person or Phone), use it
    if (desiredMeetingTypeId) {
      const agentHasType = slot.availableAgents?.some((a) =>
        a.meetingTypes?.some((t) => t.id === desiredMeetingTypeId)
      );
      if (agentHasType) return desiredMeetingTypeId;
    }
    // Otherwise try to keep previous selection
    if (previousTimeslotMeetingTypeId) {
      const exists = slot.availableAgents?.some((a) =>
        a.meetingTypes?.some((t) => t.id === previousTimeslotMeetingTypeId)
      );
      if (exists) return previousTimeslotMeetingTypeId;
    }
    // Fallback to first available meeting type for first agent
    return slot.availableAgents?.[0]?.meetingTypes?.[0]?.id;
  };

  if (matchingTimeslot) {
    const chosenMeetingTypeId = resolveMeetingTypeId(
      matchingTimeslot,
      meetingTypeId,
      prevSelectedTimeslot?.timeslotMeetingTypeId
    );

    const keepAgent = isAgentValidForTimeslot(matchingTimeslot, prevAgentId, chosenMeetingTypeId);

    const selectedTimeslot = matchingTimeslot as ISelectedTimeslot;
    selectedTimeslot.timeslotMeetingTypeId = chosenMeetingTypeId;

    return {
      availableTimeslots,
      selectedTimeslot,
      agentId: keepAgent
        ? prevAgentId
        : getAgentByPreselectRuleForAppointment(matchingTimeslot.availableAgents, chosenMeetingTypeId),
    };
  }

  // Fallback to first available timeslot
  const first = availableTimeslots[0];
  const chosenMeetingTypeId = resolveMeetingTypeId(first, meetingTypeId, prevSelectedTimeslot?.timeslotMeetingTypeId);
  const selectedTimeslot = first as ISelectedTimeslot;
  selectedTimeslot.timeslotMeetingTypeId = chosenMeetingTypeId;

  return {
    availableTimeslots,
    selectedTimeslot,
    agentId: getAgentByPreselectRuleForAppointment(first?.availableAgents, chosenMeetingTypeId),
  };
};
